import { MouseEventHandler, CSSProperties } from 'react';
import styled from 'styled-components';
import InterfaceArrowsButtonUp from '@streamlinehq/streamlinehq/img/streamline-micro-line/interface-essential/arrows/interface-arrows-button-up.svg';
import { BouSvgIcon } from '@upright/ui/common/generic/components/bou-svg-icon';
import { Row } from '@tanstack/react-table';

type RowExpandedTogglerProps = Pick<Row<unknown>, 'getCanExpand' | 'toggleExpanded' | 'getIsExpanded' | 'depth'> & {
  children?: JSX.Element | string;
  tabWidthEm?: number;
  onClick?: MouseEventHandler;
  style?: CSSProperties;
  /**
   * When true, only the chevron toggles expand/collapse — clicking the row body
   * (name / description) does nothing. Opt-in; defaults to the whole-row click
   * behaviour so existing tables are unaffected. Used by the PLG segment editor
   * so double-clicking a name to rename it isn't swallowed by an expand toggle
   * (PLT-5707).
   */
  expandOnChevronOnly?: boolean;
};

export function RowExpandedToggler(props: RowExpandedTogglerProps) {
  const defaultOnClick = () => {
    props.toggleExpanded(!props.getIsExpanded());
  };
  const handleClick = props.onClick ?? defaultOnClick;
  const chevronOnly = props.expandOnChevronOnly === true;
  return (
    <CellRoot
      canExpand={props.getCanExpand() && !chevronOnly}
      onClick={chevronOnly ? undefined : handleClick}
      style={props.style}
    >
      <Toggler {...props} onChevronClick={chevronOnly ? handleClick : undefined} />
      {/* min-width:0 lets long content wrap instead of forcing the row wider,
          which would otherwise shrink the fixed-width indent column (PLT-5707). */}
      <div style={{ flexGrow: 1, minWidth: 0 }}>{props.children}</div>
    </CellRoot>
  );
}

const CellRoot = styled.div<{ canExpand: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: ${props => (props.canExpand ? 'pointer' : undefined)};
`;

function Toggler(props: RowExpandedTogglerProps & { onChevronClick?: MouseEventHandler }) {
  const tabWidthEm = props.tabWidthEm ?? 1.5;
  const width = toEm((props.depth + 1) * tabWidthEm);
  const chevronWidth = toEm(tabWidthEm);
  const clickable = Boolean(props.onChevronClick) && props.getCanExpand();
  return (
    <ChevronWrapper
      style={{ width }}
      $clickable={clickable}
      onClick={
        props.onChevronClick
          ? event => {
              // Stop the click from bubbling to any row-level handler and toggle here.
              event.stopPropagation();
              props.onChevronClick?.(event);
            }
          : undefined
      }
    >
      {props.getCanExpand() && <Chevron style={{ width: chevronWidth }} {...props} />}
    </ChevronWrapper>
  );
}

const ChevronWrapper = styled.div<{ $clickable?: boolean }>`
  flex-grow: 0;
  // Keep the indent width fixed so long titles don't squeeze it and misalign
  // rows at the same depth (PLT-5707).
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  cursor: ${props => (props.$clickable ? 'pointer' : undefined)};
`;

type ChevronProps = Pick<RowExpandedTogglerProps, 'getIsExpanded'> & { style?: CSSProperties };
function Chevron(props: ChevronProps) {
  const rotation = props.getIsExpanded() ? 180 : 90;
  return (
    <BouSvgIcon
      size="0.8em"
      icon={<RotatingChevronIcon rotation={rotation} />}
      style={{ display: 'flex', ...props.style }}
    />
  );
}

const RotatingChevronIcon = styled(InterfaceArrowsButtonUp)<{ rotation: number }>`
  transition-timing-function: ease-out;
  transition-duration: 0.2s;
  transform: ${props => `rotate(${props.rotation}deg)`};
`;

const toEm = (value: number) => `${value}em`;
